import { fontSmoothing, getFontFamily } from '../../typography';
import idsTokensDefault from '../../utils/tokens/idsTokens';

export const MuiDateCalendar = {
  root: {
    maxHeight: 'unset',
    height: '100%',
  },
};

/**
 * Element that wraps the day view
 */
export const MuiDayCalendar = (idsTokens = idsTokensDefault) => ({
  header: {
    paddingBottom: idsTokens.datePicker.default.spacing.calendarGap,
  },
  weekDayLabel: {
    fontFamily: getFontFamily(idsTokens),
    ...fontSmoothing,
    width: 40,
    height: 24,
    fontSize: idsTokens.typography.font.size.base,
    fontWeight: idsTokens.typography.font.weight.bold,
    letterSpacing: idsTokens.typography.spacing.letter.none,
    color: idsTokens.datePicker.default.color.typography.disabled,
    margin: 0,
    lineHeight: idsTokens.typography.lineHeight.base,
  },
  weekContainer: {
    margin: 0,
    backgroundColor: idsTokens.datePicker.default.color.calendar.container,
  },
  monthContainer: {
    overflow: 'hidden',
  },
});

/**
 * Each individual day in the day view
 */
export const MuiPickersDay = (idsTokens = idsTokensDefault) => ({
  root: {
    fontFamily: getFontFamily(idsTokens),
    ...fontSmoothing,
    width: 40,
    height: 40,
    borderRadius: '50%',
    fontSize: idsTokens.typography.font.size.base,
    fontWeight: idsTokens.typography.font.weight.medium,
    lineHeight: idsTokens.typography.lineHeight.base,
    letterSpacing: idsTokens.typography.spacing.letter.lg,
    color: idsTokens.datePicker.default.color.typography.enabled,
    backgroundColor: idsTokens.datePicker.default.color.calendar.container,
    maxWidth: idsTokens.datePicker.default.sizing.dayMaxWidth,
    maxHeight: idsTokens.datePicker.default.sizing.dayMaxHeight,
    '&:not(.Mui-selected)': {
      border: 'unset',
    },
    '&:focus, &:hover': {
      backgroundColor: idsTokens.datePicker.default.color.option.fillEnabledHover,
      color: idsTokens.datePicker.default.color.typography.hover,
    },
    '&.Mui-selected': {
      color: idsTokens.datePicker.default.color.typography.selected,
      backgroundColor: idsTokens.datePicker.default.color.option.fillSelected,
      outline: 'unset',
      '&:hover': {
        backgroundColor: idsTokens.datePicker.default.color.option.fillSelectedHover,
        color: idsTokens.datePicker.default.color.typography.selected,
      },
    },
    '&.Mui-disabled': {
      color: idsTokens.datePicker.default.color.typography.disabled,
    },
  },
  dayWithMargin: {
    margin: 0,
  },
  today: {
    backgroundColor: idsTokens.datePicker.default.color.calendar.container,
    outline: `1px solid ${idsTokens.datePicker.default.color.dayOption.todayHover}`,
    border: 'unset',
    outlineOffset: -4,
    '&:hover': {
      backgroundColor: idsTokens.datePicker.default.color.option.fillEnabledHover,
      outline: `1px solid ${idsTokens.datePicker.default.color.dayOption.todayHover}`,
      outlineOffset: -4,
      transition: 'background-color 0.2s cubic-bezier(0.38, 0.0, 1, 1)',
    },
    '&.Mui-selected': {
      outline: `1px solid ${idsTokens.datePicker.default.color.dayOption.todaySelected}`,
      outlineOffset: -4,
      transition: 'background-color 0.2s cubic-bezier(0.38, 0.0, 1, 1)',
    },
  },
});

/**
 * Element that wraps the whole DatePicker component
 */
export const MuiPickersLayout = (idsTokens = idsTokensDefault) => ({
  root: {
    minWidth: idsTokens.datePicker.default.sizing.containerMinWidth,
    width: idsTokens.datePicker.default.sizing.containerMinWidth,
  },
});

/**
 * The calendar Header
 */
export const MuiPickersCalendarHeader = (idsTokens = idsTokensDefault) => ({
  labelContainer: {
    margin: 'auto',
    zIndex: 1,
    borderRadius: 4,
    paddingLeft: 8,
    paddingRight: 8,
    overflow: 'visible',
    '& > button': {
      display: 'none',
    },
  },
  label: {
    fontSize: idsTokens.typography.font.size.base,
    fontWeight: idsTokens.typography.font.weight.bold,
    lineHeight: idsTokens.typography.lineHeight.base,
    letterSpacing: idsTokens.typography.spacing.letter.none,
    color: idsTokens.button.default.color.discreet.content.enabled,
    opacity: 1,
    marginRight: 0,
    marginTop: 2,
    borderRadius: idsTokens.button.default.radius.standard.discreet,
    paddingTop: idsTokens.button.default.spacing.standard.discreet.padding.top,
    paddingRight: idsTokens.button.default.spacing.standard.discreet.padding.right,
    paddingBottom: idsTokens.button.default.spacing.standard.discreet.padding.bottom,
    paddingLeft: idsTokens.button.default.spacing.standard.discreet.padding.left,
    '&:hover': {
      backgroundColor: idsTokens.button.default.color.discreet.background.hover,
      color: idsTokens.button.default.color.discreet.content.hover,
    },
  },
  root: {
    paddingBottom: idsTokens.datePicker.default.spacing.calendarGap,
    paddingTop: idsTokens.datePicker.default.spacing.dialogPadding,
    height: '48px',
    marginBottom: 0,
    maxHeight: 'unset',
    paddingLeft: 'unset',
    paddingRight: 'unset',
    margin: 'unset',
  },
});

/**
 * Element that wraps the whole month view
 */
export const MuiMonthCalendar = (idsTokens = idsTokensDefault) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    gridTemplateRows: 'repeat(6, auto)',
    justifyContent: 'center',
    alignContent: 'center',
    columnGap: 0,
    rowGap: '22px',
  },

  button: {
    fontFamily: getFontFamily(idsTokens),
    ...fontSmoothing,
    borderRadius: idsTokens.datePicker.default.radius.option,
    fontWeight: idsTokens.typography.font.weight.medium,
    fontSize: idsTokens.typography.font.size.base,
    lineHeight: idsTokens.typography.lineHeight.base,
    letterSpacing: idsTokens.typography.spacing.letter.none,
    margin: 0,
    padding: '6px 16px',
    maxHeight: idsTokens.datePicker.default.sizing.optionMaxHeight,
    minWidth: idsTokens.datePicker.default.sizing.optionMinWidth,
    color: idsTokens.datePicker.default.color.typography.enabled,
    backgroundColor: 'transparent',
    width: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    justifySelf: 'end',
    '&:nth-of-type(2n)': {
      justifySelf: 'start',
    },
    '&:focus, &:hover': {
      backgroundColor: idsTokens.datePicker.default.color.option.fillEnabledHover,
      color: idsTokens.datePicker.default.color.typography.hover,
    },
    '&.Mui-selected': {
      color: idsTokens.datePicker.default.color.typography.selected,
      backgroundColor: idsTokens.datePicker.default.color.option.fillSelected,
      '&:hover': {
        backgroundColor: idsTokens.datePicker.default.color.option.fillSelectedHover,
        color: idsTokens.datePicker.default.color.typography.selected,
      },
    },
    '&.Mui-disabled': {
      color: idsTokens.datePicker.default.color.typography.disabled,
    },
  },
});

/**
 * Element that wraps the whole year view
 */
export const MuiYearCalendar = (idsTokens = idsTokensDefault) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    rowGap: idsTokens.datePicker.default.spacing.optionRowGap,
    columnGap: 0,
    alignSelf: 'stretch',
    flexWrap: 'wrap',
  },
  button: {
    fontFamily: getFontFamily(idsTokens),
    ...fontSmoothing,
    height: 32,
    margin: 0,
    fontWeight: idsTokens.typography.font.weight.medium,
    fontSize: idsTokens.typography.font.size.base,
    lineHeight: idsTokens.typography.lineHeight.base,
    letterSpacing: idsTokens.typography.spacing.letter.none,
    marginBottom: idsTokens.datePicker.default.spacing.optionRowGap,
    minWidth: idsTokens.datePicker.default.sizing.optionMinWidth,
    maxHeight: idsTokens.datePicker.default.sizing.optionMaxHeight,
    borderRadius: idsTokens.datePicker.default.radius.option,
    color: idsTokens.datePicker.default.color.typography.enabled,
    '&:focus, &:hover': {
      backgroundColor: idsTokens.datePicker.default.color.option.fillEnabledHover,
      color: idsTokens.datePicker.default.color.typography.hover,
    },
    '&.Mui-selected': {
      color: idsTokens.datePicker.default.color.typography.selected,
      backgroundColor: idsTokens.datePicker.default.color.option.fillSelected,
      '&:hover': {
        backgroundColor: idsTokens.datePicker.default.color.option.fillSelectedHover,
        color: idsTokens.datePicker.default.color.typography.selected,
      },
    },
    '&.Mui-disabled': {
      color: idsTokens.datePicker.default.color.typography.disabled,
    },
  },
});

/**
 * The switcher that allows you to go from one month to the next.
 * It is inside the calendar header.
 */
export const MuiPickersArrowSwitcher = {
  root: {
    boxSizing: 'border-box',
    position: 'absolute',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    left: 0,
  },
  button: {
    width: 32,
    height: 32,
    '&.MuiPickersArrowSwitcher-previousIconButton': {
      marginLeft: 16,
    },
    '&.MuiPickersArrowSwitcher-nextIconButton': {
      marginRight: 16,
    },
  },
};
